import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const NARRATORS = [
  { id: 'aria',      label: 'Aria',      voiceId: '9BWtsMINqrJLrRacOk9x', gender: 'F', desc: 'Warm & hypnotic' },
  { id: 'sarah',     label: 'Sarah',     voiceId: 'EXAVITQu4vr4xnSDxMaL', gender: 'F', desc: 'Soft & breathy' },
  { id: 'laura',     label: 'Laura',     voiceId: 'FGY2WhTYpPnrIDTdsKH5', gender: 'F', desc: 'Clear & intimate' },
  { id: 'charlotte', label: 'Charlotte', voiceId: 'XB0fDUnXU5powFXDhCwa', gender: 'F', desc: 'British & literary' },
  { id: 'matilda',   label: 'Matilda',   voiceId: 'XrExE9yKIg1WjnnlVkGX', gender: 'F', desc: 'Playful & charged' },
  { id: 'adam',      label: 'Adam',      voiceId: 'pNInz6obpgDQGcFmaJgB', gender: 'M', desc: 'Deep & commanding' },
  { id: 'antoni',    label: 'Antoni',    voiceId: 'ErXwobaYiN019PkySvjV', gender: 'M', desc: 'Smooth & seductive' },
  { id: 'thomas',    label: 'Thomas',    voiceId: 'GBv7mTt0atIp3Br8iCZE', gender: 'M', desc: 'Dark & brooding' },
  { id: 'george',    label: 'George',    voiceId: 'JBFqnCBsd6RMkjVDRZzb', gender: 'M', desc: 'Rich & unhurried' },
];

const MOODS = [
  { id: 'longing',   label: 'Longing',   em: '🌙' },
  { id: 'electric',  label: 'Electric',  em: '⚡' },
  { id: 'tender',    label: 'Tender',    em: '🌸' },
  { id: 'forbidden', label: 'Forbidden', em: '🥀' },
  { id: 'nostalgic', label: 'Nostalgic', em: '🕯️' },
  { id: 'wild',      label: 'Wild',      em: '🌊' },
];

const SETTINGS = [
  { id: 'paris',  label: 'Paris, 2am' },
  { id: 'hotel',  label: 'Strangers in a Hotel' },
  { id: 'rain',   label: 'Rainy City Night' },
  { id: 'cabin',  label: 'Secluded Cabin' },
  { id: 'office', label: 'After Hours at the Office' },
  { id: 'beach',  label: 'Deserted Beach' },
];

const C = {
  black:  '#09080c',
  deep:   '#110f16',
  card:   '#15121e',
  raised: '#1d1929',
  violet: '#5a3d99',
  accent: '#7c5cbf',
  lilac:  '#c9a0e8',
  ivory:  '#f2ece0',
  muted:  'rgba(242,236,224,0.38)',
  faint:  'rgba(242,236,224,0.08)',
  border: 'rgba(242,236,224,0.07)',
  gold:   '#e2b96a',
};
const font = {
  display: "'DM Serif Display', serif",
  body:    "'Playfair Display', serif",
  ui:      "'Inter', sans-serif",
};

export default function Taster() {
  const navigate = useNavigate();
  const [view, setView]   = useState('configure'); // configure | generating | player
  const [error, setError] = useState('');
  const [story, setStory] = useState(null);
  const [isPlaying, setIsPlaying]   = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const audioRef = useRef(null);

  const [config, setConfig] = useState({
    mood: '', customMood: '',
    setting: '', customSetting: '',
    customTheme: '',
    customDetails: '',
    narrator: '',
    intensity: 3,
    pov: 'second',
    yourName: '', loverName: '',
    yourGender: '', loverGender: '',
  });

  const canGenerate = (config.mood || config.customMood) && (config.setting || config.customSetting) && config.narrator;

  const previewVoice = (voiceId) => {
    if (window._previewLoading) return;
    if (window._previewAudio) { window._previewAudio.pause(); window._previewAudio = null; }
    window._previewLoading = true;
    fetch(`${process.env.REACT_APP_API_URL}/api/generate/preview/${voiceId}?t=${Date.now()}`)
      .then(r => r.blob())
      .then(b => {
        const a = new Audio(URL.createObjectURL(b));
        window._previewAudio = a;
        window._previewLoading = false;
        a.play();
        a.onended = () => { window._previewAudio = null; };
      })
      .catch(() => { window._previewLoading = false; });
  };

  const generate = async () => {
    if (!canGenerate) return;
    setError(''); setView('generating');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/generate/taster`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mood: config.mood, customMood: config.customMood,
          setting: config.setting, customSetting: config.customSetting,
          customTheme: config.customTheme,
          narrator: config.narrator,
          intensity: config.intensity, pov: config.pov,
          yourName: config.yourName, loverName: config.loverName,
          yourGender: config.yourGender, loverGender: config.loverGender,
          customDetails: config.customDetails,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      // Convert base64 audio to blob URL for Safari compatibility
      if (data.story?.audioUrl?.startsWith('data:audio')) {
        const base64 = data.story.audioUrl.split(',')[1];
        const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'audio/mpeg' });
        data.story.audioUrl = URL.createObjectURL(blob);
      }
      setStory(data);
      setView('player');
    } catch (e) {
      setError(e.message);
      setView('configure');
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoad = () => setDuration(audio.duration);
    const onEnd  = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoad);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoad);
      audio.removeEventListener('ended', onEnd);
    };
  }, [story]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); setIsPlaying(false); }
    else           { audio.play();  setIsPlaying(true);  }
  };

  const fmt = (s) => `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`;

  const s = {
    page: { minHeight: '100vh', background: C.black, color: C.ivory, fontFamily: font.ui, fontWeight: 300 },
    label: { fontSize: '9px', letterSpacing: '4px', textTransform: 'uppercase', color: C.accent, opacity: 0.9, marginBottom: '10px' },
    input: (filled) => ({
      width: '100%', padding: '12px 14px',
      background: C.raised,
      border: `1px solid ${filled ? 'rgba(124,92,191,0.45)' : 'rgba(124,92,191,0.18)'}`,
      borderRadius: '13px', color: C.ivory, fontSize: '13px',
      fontFamily: font.body, fontStyle: 'italic', outline: 'none', boxSizing: 'border-box',
    }),
    moodTile: (active) => ({
      padding: '12px 5px', borderRadius: '13px',
      border: `1px solid ${active ? 'rgba(124,92,191,0.5)' : C.border}`,
      background: active ? 'rgba(90,61,153,0.14)' : 'rgba(255,255,255,0.02)',
      textAlign: 'center', cursor: 'pointer',
    }),
    settingRow: (active) => ({
      padding: '11px 14px', borderRadius: '12px', cursor: 'pointer',
      border: `1px solid ${active ? 'rgba(124,92,191,0.45)' : C.border}`,
      background: active ? 'rgba(90,61,153,0.1)' : 'rgba(255,255,255,0.02)',
      fontFamily: font.body, fontStyle: 'italic', fontSize: '13px',
      color: active ? C.ivory : C.muted,
    }),
    narRow: (active) => ({
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '11px 12px', borderRadius: '12px', cursor: 'pointer',
      border: `1px solid ${active ? 'rgba(124,92,191,0.45)' : C.border}`,
      background: active ? 'rgba(90,61,153,0.08)' : 'rgba(255,255,255,0.02)',
      marginBottom: '7px',
    }),
    gBtn: (active) => ({
      flex: 1, padding: '6px 2px', borderRadius: '7px',
      border: `1px solid ${active ? 'rgba(124,92,191,0.5)' : C.border}`,
      background: active ? 'rgba(90,61,153,0.2)' : 'rgba(255,255,255,0.02)',
      color: active ? C.lilac : C.muted, fontSize: '11px', cursor: 'pointer', fontFamily: font.ui,
    }),
    btnP: (on) => ({
      width: '100%', padding: '16px',
      background: on ? 'linear-gradient(135deg, #5a3d99, #7c5cbf)' : 'rgba(90,61,153,0.15)',
      border: 'none', borderRadius: '13px',
      color: on ? C.ivory : 'rgba(242,236,224,0.25)',
      fontFamily: font.display, fontStyle: 'italic', fontSize: '19px',
      cursor: on ? 'pointer' : 'not-allowed',
      boxShadow: on ? '0 8px 28px rgba(90,61,153,0.3)' : 'none',
    }),
  };

  // ── CONFIGURE ────────────────────────────────────────────────────────────
  if (view === 'configure') return (
    <div style={s.page}>
      <div style={{ padding: '22px 22px 100px' }}>
        {/* Header */}
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '11px', cursor: 'pointer', letterSpacing: '1px', marginBottom: '20px', display: 'block', fontFamily: font.ui }}>← Back</button>

        <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '32px', marginBottom: '4px' }}>Free Story</div>
        <div style={{ fontSize: '12px', color: C.muted, lineHeight: 1.6, marginBottom: '28px' }}>
          A taste of what YourSiren can write for you. No account needed.
        </div>

        {/* Mood */}
        <div style={{ marginBottom: '20px' }}>
          <div style={s.label}>Mood</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '7px', marginBottom: '9px' }}>
            {MOODS.map(m => (
              <div key={m.id} onClick={() => setConfig(c => ({ ...c, mood: c.mood === m.id ? '' : m.id, customMood: '' }))} style={s.moodTile(config.mood === m.id)}>
                <span style={{ fontSize: '17px', display: 'block', marginBottom: '3px' }}>{m.em}</span>
                <span style={{ fontSize: '10px', color: config.mood === m.id ? C.lilac : C.muted }}>{m.label}</span>
              </div>
            ))}
          </div>
          <input value={config.customMood} onChange={e => setConfig(c => ({ ...c, customMood: e.target.value, mood: '' }))} placeholder="Or describe your own mood…" style={s.input(!!config.customMood)} />
        </div>

        {/* Setting */}
        <div style={{ marginBottom: '20px' }}>
          <div style={s.label}>Setting</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '9px' }}>
            {SETTINGS.map(setting => (
              <div key={setting.id} onClick={() => setConfig(c => ({ ...c, setting: c.setting === setting.id ? '' : setting.id, customSetting: '' }))} style={s.settingRow(config.setting === setting.id)}>
                {setting.label}
              </div>
            ))}
          </div>
          <input value={config.customSetting} onChange={e => setConfig(c => ({ ...c, customSetting: e.target.value, setting: '' }))} placeholder="Or describe your own setting…" style={s.input(!!config.customSetting)} />
        </div>

        {/* Tell us anything */}
        <div style={{ background: C.card, border: `1px solid rgba(124,92,191,0.22)`, borderRadius: '16px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px' }}>
            <span style={{ color: C.lilac, fontSize: '13px' }}>✦</span>
            <div style={s.label}>Tell us anything</div>
          </div>
          <div style={{ fontSize: '12px', color: C.muted, fontFamily: font.body, fontStyle: 'italic', marginBottom: '11px', lineHeight: 1.6 }}>
            Describe a fantasy, a dynamic, a detail — anything you want in your story.
          </div>
          <textarea
            rows={3}
            value={config.customTheme}
            onChange={e => setConfig(c => ({ ...c, customTheme: e.target.value }))}
            placeholder="e.g. We've been pretending nothing's happening for months…"
            style={{ ...s.input(!!config.customTheme), resize: 'none', lineHeight: 1.6 }}
          />
          <div style={{ marginTop: '10px', fontSize: '12px', color: 'rgba(242,236,224,0.25)', fontFamily: "Inter, sans-serif", letterSpacing: '0.5px', marginBottom: '6px' }}>Anything else to weave in?</div>
          <textarea
            rows={2}
            value={config.customDetails}
            onChange={e => setConfig(c => ({ ...c, customDetails: e.target.value }))}
            placeholder="A specific detail, feeling, or fantasy…"
            style={{ ...s.input(!!config.customDetails), resize: 'none', lineHeight: 1.6 }}
          />
        </div>

        {/* Characters */}
        <div style={{ background: C.card, border: `1px solid rgba(124,92,191,0.2)`, borderRadius: '16px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
            <span style={{ color: C.lilac, fontSize: '13px' }}>✦</span>
            <div style={s.label}>Make it yours</div>
          </div>
          <div style={{ fontSize: '12px', color: C.muted, fontFamily: font.body, fontStyle: 'italic', marginBottom: '12px', lineHeight: 1.5 }}>
            Add names and the story is written around them.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-7px', left: '10px', fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: C.accent, background: C.card, padding: '0 4px' }}>You</div>
              <input value={config.yourName} onChange={e => setConfig(c => ({ ...c, yourName: e.target.value }))} placeholder="Your name…" style={s.input(!!config.yourName)} />
            </div>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-7px', left: '10px', fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: C.accent, background: C.card, padding: '0 4px' }}>Them</div>
              <input value={config.loverName} onChange={e => setConfig(c => ({ ...c, loverName: e.target.value }))} placeholder="Their name…" style={s.input(!!config.loverName)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <div style={{ fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(242,236,224,0.2)', marginBottom: '5px', fontFamily: font.ui }}>Your gender</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['Female','Male','Neutral'].map(g => <button key={g} onClick={() => setConfig(c => ({ ...c, yourGender: c.yourGender === g ? '' : g }))} style={s.gBtn(config.yourGender === g)}>{g[0]}</button>)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(242,236,224,0.2)', marginBottom: '5px', fontFamily: font.ui }}>Their gender</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['Female','Male','Neutral'].map(g => <button key={g} onClick={() => setConfig(c => ({ ...c, loverGender: c.loverGender === g ? '' : g }))} style={s.gBtn(config.loverGender === g)}>{g[0]}</button>)}
              </div>
            </div>
          </div>
        </div>

        {/* Narrator */}
        <div style={{ marginBottom: '24px' }}>
          <div style={s.label}>Narrator</div>
          <div style={{ fontSize: '10px', color: 'rgba(242,236,224,0.22)', marginBottom: '8px', letterSpacing: '1px' }}>Female</div>
          {NARRATORS.filter(n => n.gender === 'F').map(n => (
            <div key={n.id} style={{ display: 'flex', gap: '7px', alignItems: 'center', marginBottom: '7px' }}>
              <div onClick={() => setConfig(c => ({ ...c, narrator: n.id }))} style={{ ...s.narRow(config.narrator === n.id), flex: 1, marginBottom: 0 }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg,rgba(90,61,153,0.4),#110f16)', border: `1px solid rgba(124,92,191,0.2)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>👩</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', marginBottom: '1px' }}>{n.label}</div>
                  <div style={{ fontSize: '10px', color: C.muted, fontFamily: font.body, fontStyle: 'italic' }}>{n.desc}</div>
                </div>
              </div>
              <button onClick={() => previewVoice(n.voiceId)} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(124,92,191,0.1)', border: `1px solid rgba(124,92,191,0.22)`, color: C.accent, cursor: 'pointer', fontSize: '11px', flexShrink: 0 }}>▶</button>
            </div>
          ))}
          <div style={{ fontSize: '10px', color: 'rgba(242,236,224,0.22)', margin: '10px 0 8px', letterSpacing: '1px' }}>Male</div>
          {NARRATORS.filter(n => n.gender === 'M').map(n => (
            <div key={n.id} style={{ display: 'flex', gap: '7px', alignItems: 'center', marginBottom: '7px' }}>
              <div onClick={() => setConfig(c => ({ ...c, narrator: n.id }))} style={{ ...s.narRow(config.narrator === n.id), flex: 1, marginBottom: 0 }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg,rgba(90,61,153,0.4),#110f16)', border: `1px solid rgba(124,92,191,0.2)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>👨</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', marginBottom: '1px' }}>{n.label}</div>
                  <div style={{ fontSize: '10px', color: C.muted, fontFamily: font.body, fontStyle: 'italic' }}>{n.desc}</div>
                </div>
              </div>
              <button onClick={() => previewVoice(n.voiceId)} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(124,92,191,0.1)', border: `1px solid rgba(124,92,191,0.22)`, color: C.accent, cursor: 'pointer', fontSize: '11px', flexShrink: 0 }}>▶</button>
            </div>
          ))}
        </div>

        {error && <div style={{ color: '#e88a8a', fontSize: '13px', marginBottom: '14px', padding: '10px 14px', background: 'rgba(232,138,138,0.08)', borderRadius: '10px', border: '1px solid rgba(232,138,138,0.15)' }}>{error}</div>}

        <button onClick={generate} disabled={!canGenerate} style={s.btnP(canGenerate)}>
          Write My Free Story
        </button>
      </div>
    </div>
  );

  // ── GENERATING ───────────────────────────────────────────────────────────
  if (view === 'generating') return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 24px' }}>
      <div>
        <div style={{ fontSize: '48px', marginBottom: '24px', animation: 'pulse 2s ease-in-out infinite' }}>✦</div>
        <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '24px', marginBottom: '10px' }}>Writing your story…</div>
        <div style={{ fontSize: '13px', color: C.muted }}>crafting every word for you</div>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.4;transform:scale(0.88)}}`}</style>
    </div>
  );

  // ── PLAYER ───────────────────────────────────────────────────────────────
  if (view === 'player' && story) {
    const progress = duration ? (currentTime / duration) * 100 : 0;
    return (
      <div style={{ ...s.page, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse 80% 40% at 50% 10%,rgba(90,61,153,0.2) 0%,transparent 60%),${C.black}`, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, padding: '22px 22px 40px', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <button onClick={() => setView('configure')} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '11px', cursor: 'pointer', letterSpacing: '1px', fontFamily: font.ui }}>← Back</button>
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '22px', padding: '22px 20px 18px', marginBottom: '20px', flex: 1 }}>
            <div style={{ fontSize: '9px', letterSpacing: '4px', textTransform: 'uppercase', color: C.accent, opacity: 0.6, marginBottom: '8px', fontFamily: font.ui }}>Your Free Story</div>
            <div style={{ fontFamily: font.body, fontStyle: 'italic', fontSize: '22px', lineHeight: 1.3, marginBottom: '5px' }}>
              {config.yourName && config.loverName
                ? `${config.yourName} & ${config.loverName}`
                : `${config.mood || config.customMood || 'Your Story'}`}
            </div>
            <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1px', marginBottom: '14px', fontFamily: font.ui }}>
              {NARRATORS.find(n => n.id === config.narrator)?.label} · {config.mood || config.customMood}
            </div>

            {(config.yourName || config.loverName) && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', background: 'rgba(90,61,153,0.1)', border: `1px solid rgba(124,92,191,0.2)`, borderRadius: '9px', padding: '9px 11px', marginBottom: '16px' }}>
                <span style={{ fontSize: '11px', flexShrink: 0, marginTop: '1px', color: C.lilac }}>✦</span>
                <div style={{ fontSize: '11px', color: C.lilac, lineHeight: 1.5, fontFamily: font.body, fontStyle: 'italic' }}>
                  Written for <strong>{config.yourName}</strong>{config.loverName ? ` & ${config.loverName}` : ''} — this story exists nowhere else.
                </div>
              </div>
            )}

            {/* Scrubable waveform */}
            <div
              onClick={(e) => {
                const audio = audioRef.current;
                if (!audio || !duration) return;
                const rect = e.currentTarget.getBoundingClientRect();
                audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
              }}
              style={{ height: '40px', display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '10px', cursor: 'pointer' }}
            >
              {Array.from({ length: 28 }).map((_, i) => {
                const h = 8 + Math.sin(i * 0.8) * 8 + Math.cos(i * 0.3) * 5;
                const pct = (i / 28) * 100;
                return <div key={i} style={{ flex: 1, borderRadius: '2px', height: `${h}px`, background: pct < progress ? (pct < progress - 4 ? 'rgba(124,92,191,0.55)' : C.lilac) : C.faint }} />;
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'rgba(242,236,224,0.2)', marginBottom: '4px' }}>
              <span>{fmt(currentTime)}</span><span>{fmt(duration)}</span>
            </div>
          </div>

          {story.audioUrl && <audio ref={audioRef} src={story.audioUrl} />}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '26px', marginBottom: '20px' }}>
            <button style={{ background: 'none', border: 'none', color: 'rgba(242,236,224,0.25)', fontSize: '16px', cursor: 'pointer' }} onClick={() => { if(audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime-10); }}>«</button>
            <button onClick={togglePlay} style={{ width: '58px', height: '58px', borderRadius: '50%', background: 'linear-gradient(135deg,#5a3d99,#7c5cbf)', border: 'none', color: C.ivory, fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 28px rgba(90,61,153,0.4)', cursor: 'pointer' }}>
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button style={{ background: 'none', border: 'none', color: 'rgba(242,236,224,0.25)', fontSize: '16px', cursor: 'pointer' }} onClick={() => { if(audioRef.current) audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime+10); }}>»</button>
          </div>

          {/* Upsell */}
          <div style={{ background: C.card, border: `1px solid rgba(124,92,191,0.2)`, borderRadius: '18px', padding: '22px', textAlign: 'center' }}>
            <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '20px', marginBottom: '8px' }}>
              Want to hear what happens?
            </div>
            <div style={{ fontSize: '12px', color: C.muted, lineHeight: 1.7, marginBottom: '18px' }}>
              Unlimited stories. Your names, your words, your world — written fresh every time.
            </div>
            <button onClick={() => navigate('/auth')} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg,#5a3d99,#7c5cbf)', border: 'none', borderRadius: '13px', color: C.ivory, fontFamily: font.display, fontStyle: 'italic', fontSize: '19px', cursor: 'pointer', boxShadow: '0 8px 28px rgba(90,61,153,0.3)', marginBottom: '10px' }}>
              Continue — $9.99/mo
            </button>
            <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'rgba(242,236,224,0.2)', fontSize: '12px', cursor: 'pointer', fontFamily: font.ui }}>
              Maybe later
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
